/**
 * nap-frontend browser telemetry logger — Phase 1 (logging only, ZERO new deps).
 *
 * Captures structured logs in the browser and ships them to the backend ingest
 * endpoint (POST {BACKEND_URL}/telemetry/logs). The backend enriches and forwards
 * them to the observability pipeline. No OpenTelemetry packages are imported here;
 * trace correlation is wired in FE Phase 2 via `setTraceContextProvider`.
 *
 * Lifecycle (call once from src/index.tsx, after the backend URL is resolved):
 *   import { initTelemetryLogger, installGlobalErrorHandlers, log } from 'telemetry/logger';
 *   initTelemetryLogger({ enabled, endpoint, environment, version });
 *   installGlobalErrorHandlers();
 *
 * Everything is a no-op until initialised with `enabled: true`, so this is safe to
 * import unconditionally and is fully controlled by runtime config (no rebuild).
 */

export type LogLevel = 'debug' | 'info' | 'warn' | 'error';

interface LogRecord {
  level: LogLevel;
  msg: string;
  ts: string;
  /** populated at log time in Phase 2 once the OTel Web SDK is present */
  trace_id?: string;
  span_id?: string;
  attrs?: Record<string, unknown>;
}

interface LoggerConfig {
  enabled: boolean;
  /** full URL, e.g. https://api.norscom-dev.norscom.cloud/telemetry/logs */
  endpoint: string;
  environment?: string;
  version?: string;
  /** capture console.log/info/debug too (noisy/PII risk). Default false. */
  captureVerboseConsole?: boolean;
}

const BATCH_MAX = 50;
const FLUSH_MS = 5000;
const MSG_MAX = 8192;
const ATTR_VALUE_MAX = 1024;
const ATTR_KEY_MAX = 32;
const MAX_DEPTH = 4;
const REDACTED = '[REDACTED]';
const SECRET_KEY_RE = /authorization|password|token|secret|cookie|api[-_]?key/i;
// Best-effort scan for obvious secret VALUES (JWTs, Bearer tokens) so a token
// passed as a bare string — not under a secret-looking key — is still redacted.
const SECRET_VALUE_RE = /Bearer\s+[\w.+/=-]+|eyJ[\w.-]{10,}/gi;

/** Per-tab session id (in-memory only; no localStorage per artifact storage rules). */
export const SESSION_ID: string = safeUuid();

let cfg: LoggerConfig | null = null;
let queue: LogRecord[] = [];
let timer: ReturnType<typeof setInterval> | null = null;
let consolePatched = false;
let handlersInstalled = false;

/** Optional hook so Phase 2 can attach the active trace context per record. */
let traceContextProvider: (() => { trace_id?: string; span_id?: string }) | null = null;
export function setTraceContextProvider(fn: () => { trace_id?: string; span_id?: string }): void {
  traceContextProvider = fn;
}

/**
 * Optional hook returning the current app JWT. When set, the ingest request
 * carries `Authorization: Bearer <jwt>` so the backend can authenticate the
 * browser and derive its tenant. Wired in index.tsx to the app token store.
 */
let authTokenProvider: (() => string | undefined) | null = null;
export function setAuthTokenProvider(fn: () => string | undefined): void {
  authTokenProvider = fn;
}

export function initTelemetryLogger(config: LoggerConfig): void {
  cfg = config;
  if (!config.enabled || !config.endpoint) return;
  if (!timer) {
    timer = setInterval(() => flush(false), FLUSH_MS);
    // Best-effort flush when the tab is hidden or unloaded.
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') flush(true);
    });
    window.addEventListener('pagehide', () => flush(true));
  }
}

/** Is logging active? */
function active(): boolean {
  return !!cfg && cfg.enabled && !!cfg.endpoint;
}

function enqueue(level: LogLevel, msg: string, attrs?: Record<string, unknown>): void {
  if (!active()) return;
  const provider = traceContextProvider;
  const tc = provider ? safe(() => provider()) : undefined;
  const enriched = enrich(attrs);
  // Trace context precedence: the active span (provider) wins; otherwise fall
  // back to ids supplied explicitly via attrs (e.g. a failed request whose span
  // is no longer the active context). Promote them to top-level and strip the
  // duplicates so they don't also surface as nap_trace_id/nap_span_id.
  const attrTraceId = typeof enriched.trace_id === 'string' ? (enriched.trace_id as string) : undefined;
  const attrSpanId = typeof enriched.span_id === 'string' ? (enriched.span_id as string) : undefined;
  delete enriched.trace_id;
  delete enriched.span_id;
  const rec: LogRecord = {
    level,
    msg: truncate(redactSecrets(String(msg)), MSG_MAX),
    ts: new Date().toISOString(),
    trace_id: tc?.trace_id ?? attrTraceId,
    span_id: tc?.span_id ?? attrSpanId,
    attrs: enriched,
  };
  queue.push(rec);
  if (queue.length >= BATCH_MAX) flush(false);
}

function enrich(attrs?: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {
    'service.name': 'nap-frontend',
    'deployment.environment': cfg?.environment,
    'service.version': cfg?.version,
    route: safe(() => window.location?.pathname),
  };
  return { ...out, ...sanitizeAttrs(attrs) };
}

function flush(useBeacon: boolean): void {
  // `active()` guarantees cfg is set; the explicit `!cfg` check also narrows the
  // type here so we don't need a non-null assertion below.
  if (!active() || !cfg || queue.length === 0) return;
  const batch = queue;
  queue = [];
  const payload = JSON.stringify({ session_id: SESSION_ID, logs: batch });
  const url = cfg.endpoint;
  const tokenProvider = authTokenProvider;
  const token = tokenProvider ? safe(() => tokenProvider()) : undefined;

  // sendBeacon survives page unload but cannot set an Authorization header, so it
  // is only used at unload time (best-effort; backend tags it as default tenant).
  if (useBeacon && !token && typeof navigator !== 'undefined' && navigator.sendBeacon) {
    try {
      navigator.sendBeacon(url, new Blob([payload], { type: 'application/json' }));
      return;
    } catch {
      /* fall through to fetch */
    }
  }
  const headers: Record<string, string> = { 'content-type': 'application/json' };
  if (token) headers.Authorization = `Bearer ${token}`;
  // Never send cookies; never let a telemetry failure surface to the app.
  // keepalive only on the unload path: the browser caps total keepalive body
  // size (~64KB shared across in-flight requests), so a large interval batch
  // could be silently dropped — interval flushes don't need it.
  void fetch(url, {
    method: 'POST',
    headers,
    body: payload,
    keepalive: useBeacon,
    credentials: 'omit',
  }).catch(() => {
    /* drop on failure — telemetry must never break the UI */
  });
}

function serializeError(err: unknown): Record<string, unknown> {
  if (err instanceof Error) {
    return {
      error_name: err.name,
      error_message: err.message,
      error_stack: truncate(err.stack || '', MSG_MAX),
    };
  }
  if (err === undefined || err === null) return {};
  return { error_message: truncate(String(err), ATTR_VALUE_MAX) };
}

/** Public structured-logging API used across the app. */
export const log = {
  debug: (msg: string, attrs?: Record<string, unknown>) => enqueue('debug', msg, attrs),
  info: (msg: string, attrs?: Record<string, unknown>) => enqueue('info', msg, attrs),
  warn: (msg: string, attrs?: Record<string, unknown>) => enqueue('warn', msg, attrs),
  error: (msg: string, attrs?: Record<string, unknown>, err?: unknown) => enqueue('error', msg, { ...attrs, ...(err ? serializeError(err) : {}) }),
};

/**
 * Capture uncaught errors, unhandled rejections, and console output.
 * Idempotent (safe under React 18 StrictMode double-invocation).
 */
export function installGlobalErrorHandlers(): void {
  if (handlersInstalled || typeof window === 'undefined') return;
  handlersInstalled = true;

  window.addEventListener('error', (e: ErrorEvent) => {
    log.error('window.onerror', { filename: e.filename, lineno: e.lineno, colno: e.colno, source: 'window.error' }, e.error ?? e.message);
  });

  window.addEventListener('unhandledrejection', (e: PromiseRejectionEvent) => {
    log.error('unhandledrejection', { source: 'unhandledrejection' }, e.reason);
  });

  patchConsole();
}

/** Wrap console so error/warn (and optionally the rest) are also forwarded. */
function patchConsole(): void {
  if (consolePatched || typeof console === 'undefined') return;
  consolePatched = true;

  const wrap = (method: 'log' | 'info' | 'warn' | 'error', level: LogLevel) => {
    const original = console[method]?.bind(console) ?? (() => undefined);
    console[method] = (...args: unknown[]) => {
      original(...args); // keep native console behaviour
      try {
        const errArg = args.find((a) => a instanceof Error);
        const msg = args
          .filter((a) => !(a instanceof Error))
          .map(stringifyArg)
          .join(' ');
        enqueue(level, msg || (errArg instanceof Error ? errArg.message : ''), {
          source: `console.${method}`,
          ...(errArg ? serializeError(errArg) : {}),
        });
      } catch {
        /* never let capture break console */
      }
    };
  };

  wrap('error', 'error');
  wrap('warn', 'warn');
  if (cfg?.captureVerboseConsole) {
    wrap('info', 'info');
    wrap('log', 'debug');
  }
}

// ----------------------------- helpers -----------------------------

/**
 * Redact secrets from log attributes. Best-effort but defence-in-depth:
 *   - drops keys matching SECRET_KEY_RE at EVERY nesting level (not just top),
 *   - scans string values for obvious secret patterns (JWTs, Bearer tokens),
 *   - the same scan is applied to `msg` at enqueue time.
 * It is heuristic, not a guarantee — unusual secret shapes can still slip
 * through, so callers should avoid logging secrets in the first place.
 */
function sanitizeAttrs(attrs?: Record<string, unknown>): Record<string, unknown> {
  if (!attrs || typeof attrs !== 'object') return {};
  const out: Record<string, unknown> = {};
  let n = 0;
  for (const [k, v] of Object.entries(attrs)) {
    if (n++ >= ATTR_KEY_MAX) break;
    if (SECRET_KEY_RE.test(k)) continue; // never forward secret-named keys
    // error_stack keeps the larger MSG_MAX budget (set by serializeError) so
    // stacks stay useful; other string values are capped tighter.
    const cap = k === 'error_stack' ? MSG_MAX : ATTR_VALUE_MAX;
    if (typeof v === 'string') out[k] = truncate(redactSecrets(v), cap);
    else if (typeof v === 'number' || typeof v === 'boolean' || v === null) out[k] = v;
    // Recurse into nested objects/arrays to strip secret-named keys before
    // stringifying, then scan the result for secret values as a final net.
    else out[k] = truncate(redactSecrets(stringifyArg(sanitizeDeep(v, 1))), cap);
  }
  return out;
}

/** Replace obvious secret substrings (JWTs, Bearer tokens) with [REDACTED]. */
function redactSecrets(s: string): string {
  return s.replace(SECRET_VALUE_RE, REDACTED);
}

/** Recursively drop secret-named keys from nested objects/arrays. Depth-capped. */
function sanitizeDeep(v: unknown, depth: number): unknown {
  if (depth > MAX_DEPTH || v === null || typeof v !== 'object') return v;
  if (Array.isArray(v)) return v.map((item) => sanitizeDeep(item, depth + 1));
  const out: Record<string, unknown> = {};
  for (const [k, val] of Object.entries(v)) {
    if (SECRET_KEY_RE.test(k)) continue;
    out[k] = sanitizeDeep(val, depth + 1);
  }
  return out;
}

function stringifyArg(a: unknown): string {
  if (typeof a === 'string') return a;
  if (a instanceof Error) return `${a.name}: ${a.message}`;
  try {
    return JSON.stringify(a);
  } catch {
    return String(a);
  }
}

function truncate(s: string, max: number): string {
  return s.length > max ? s.slice(0, max) + '…' : s;
}

function safe<T>(fn: () => T): T | undefined {
  try {
    return fn();
  } catch {
    return undefined;
  }
}

function safeUuid(): string {
  try {
    if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
      return crypto.randomUUID();
    }
  } catch {
    /* ignore */
  }
  return 'sess-' + Math.random().toString(36).slice(2) + Date.now().toString(36);
}
